import ReactJson from "react-json-view";
import { useState } from "react";
import {
  AccountingEventsGroup,
  CanonicalBusinessEvent,
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../../types";
import DigitalBusinessDocumentView from "./document/DigitalBusinessDocumentView";
import { DigitalCostDocumentView } from "./document/DigitalCostDocumentView";
import { DigitalPaymentDocumentView } from "./document/DigitalPaymentDocumentView";
import { AccountingEventsView } from "./AccountingEventsView";
import {
  isDigitalBusinessDocument,
  isDigitalCostDocument,
  isDigitalPaymentDocument,
  getEventVersion,
} from "../../helpers/typeChecks";
import { JsonModal } from "../common/JsonModal";
import { useReactJsonProps } from "../../hooks/useReactJsonProps";
import { setModalParam, clearModalParam, readInitialModal } from "../../helpers/modalUrlUtils";
import { ModalSubtitle } from "../common/ModalSubtitle";

interface EventResultsProps {
  eventData:
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | DigitalPaymentDocument;
  cbeData?: CanonicalBusinessEvent[];
}

export default function EventResults({ eventData, cbeData }: EventResultsProps) {
  const [isJsonModalVisible, setIsJsonModalVisible] = useState(
    () => readInitialModal().modal === "event-json"
  );

  const jsonViewProps = useReactJsonProps({ sortKeys: true, collapsed: 3 });

  if ("accountingEvents" in eventData) {
    return (
      <>
        <AccountingEventsView
          accountingGroup={eventData}
          accountingEvents={eventData.accountingEvents}
          jsonViewProps={jsonViewProps}
          modalHandler={() => { setIsJsonModalVisible(true); setModalParam("event-json", { eventVersion: String(getEventVersion(eventData)) }); }}
        />
        <JsonModal
          title={<>Accounting Events · V{eventData.sk}<ModalSubtitle text={eventData.pk} /></>}
          content={eventData}
          visible={isJsonModalVisible}
          onDismiss={() => { setIsJsonModalVisible(false); clearModalParam(); }}
        />
      </>
    );
  }

  if (isDigitalBusinessDocument(eventData)) {
    return (
      <>
        <DigitalBusinessDocumentView
          dbd={eventData}
          cbeData={cbeData}
          modalHandler={() => { setIsJsonModalVisible(true); setModalParam("event-json", { eventVersion: String(getEventVersion(eventData)) }); }}
        />
        <JsonModal
          title={<>Digital Business Document · V{eventData.version}<ModalSubtitle text={eventData.id} /></>}
          content={eventData.body}
          visible={isJsonModalVisible}
          onDismiss={() => { setIsJsonModalVisible(false); clearModalParam(); }}
        />
      </>
    );
  }

  if (isDigitalPaymentDocument(eventData)) {
    return (
      <DigitalPaymentDocumentView dpd={eventData} jsonViewProps={jsonViewProps} />
    );
  }

  if (isDigitalCostDocument(eventData)) {
    return (
      <DigitalCostDocumentView dcd={eventData} jsonViewProps={jsonViewProps} />
    );
  }

  return <ReactJson src={eventData} {...jsonViewProps} />;
}
